import { createStep } from '@/guides/helpers/guide-editor.util';
import { getActiveUser } from '@/data/session/session-globals';
import { GUIDE_POSITION } from '@/surveys/constants/surveys.constants';
import { $can } from '@pendo/permissions';
import { BuildingBlock, BuildingBlockLayouts, BuildingBlockProperties, SurveyBlocksDarkMode } from '@pendo/services/BuildingBlocks';
import keyBy from 'lodash/keyBy';

const STYLE_PROPERTY_NAMES = new Set(BuildingBlockProperties.styleProperties);
const WEB_STYLE_KEYS = ['pseudoStyles', 'darkModePseudoStyles', 'labelStyles', 'darkModeLabelStyles', 'darkModeDescription', 'themeStyle', 'semanticTag'];

export function getStandaloneNpsGuides (surveys, npsGuides) {
    const surveyDeliveryIds = new Set(
        Object.values(surveys).flatMap(
            (survey) => survey.delivery?.map((delivery) => delivery.deliveryId) ?? [],
        ),
    );

    return npsGuides.filter((guide) => !surveyDeliveryIds.has(guide.id));
}

export function buildSurveyLayout ({ steps, items }) {
    return steps.map((step) => ({
        id: step.id,
        ...(step.displayAttributes ? { displayAttributes: step.displayAttributes } : {}),
        items: step.surveyItemIds.map((id) => items[id]),
    }));
}

function hasSurveyWrapper (container) {
    return (container.views || []).some((child) => child.widget === 'SurveyWrapper');
}

export function destructureSurveyLayout (layout) {
    return layout.reduce(
        (acc, step) => {
            acc.items = {
                ...acc.items,
                ...keyBy(step.items, 'id'),
            };

            acc.steps.push({
                id: step.id,
                ...(step.displayAttributes ? { displayAttributes: step.displayAttributes } : {}),
                surveyItemIds: step.items.map(({ id }) => id),
            });

            return acc;
        },
        {
            steps: [],
            items: {},
        },
    );
}

export function findGuideContainer (block) {
    if (!block || typeof block !== 'object') return null;

    if (block.web?.domId === 'pendo-guide-container') return block;

    for (const child of (block.views || [])) {
        const found = findGuideContainer(child);

        if (found) return found;
    }

    return null;
}

const SURVEY_WIDGETS = new Set(['CloseButton', 'SurveyWrapper']);

function isSurveyBlock (block) {
    if (SURVEY_WIDGETS.has(block.widget)) return true;

    const domId = block.web?.domId || '';

    return domId === 'survey-button-wrapper' || domId.startsWith('survey-item-wrapper__');
}

function pushInsertionsForSurveyIndex (insertions, newSurveyIndex, existingLastSurveyIndex, maxNewSurveyIndex, newHasTrailingContent, result) {
    for (const { block, afterSurveyIndex } of insertions) {
        const wasAfterLastInExisting = afterSurveyIndex === existingLastSurveyIndex;
        const skipUseNewTrailing = wasAfterLastInExisting && newHasTrailingContent;

        if (!skipUseNewTrailing) {
            const clampedIndex = Math.min(afterSurveyIndex, maxNewSurveyIndex);

            if (clampedIndex === newSurveyIndex) {
                result.push(block);
            }
        }
    }
}

export function mergeNonSurveyBlocks (existingBuildingBlocks, newBuildingBlocks) {
    let existing;

    try {
        existing = typeof existingBuildingBlocks === 'string'
            ? JSON.parse(existingBuildingBlocks)
            : existingBuildingBlocks;
    } catch {
        return newBuildingBlocks;
    }

    const existingContainer = findGuideContainer(existing);
    const newContainer = findGuideContainer(newBuildingBlocks);

    if (!existingContainer || !newContainer) return newBuildingBlocks;

    if (!hasSurveyWrapper(newContainer)) return newBuildingBlocks;

    const existingSurveyCount = (existingContainer.views || []).filter((c) => isSurveyBlock(c)).length;
    const existingLastSurveyIndex = existingSurveyCount - 1;

    const insertions = [];
    let precedingSurveyIndex = -1;

    for (const child of (existingContainer.views || [])) {
        if (isSurveyBlock(child)) {
            precedingSurveyIndex++;
        } else {
            insertions.push({ block: child, afterSurveyIndex: precedingSurveyIndex });
        }
    }

    if (insertions.length === 0) return newBuildingBlocks;

    const newChildren = newContainer.views || [];
    const newSurveyCount = newChildren.filter((c) => isSurveyBlock(c)).length;
    const maxNewSurveyIndex = newSurveyCount - 1;

    let lastSurveyIndexInNew = -1;
    for (let i = 0; i < newChildren.length; i++) {
        if (isSurveyBlock(newChildren[i])) lastSurveyIndexInNew = i;
    }
    const newHasTrailingContent = lastSurveyIndexInNew >= 0 && lastSurveyIndexInNew < newChildren.length - 1;

    const result = [];

    for (const { block } of insertions.filter((ins) => ins.afterSurveyIndex === -1)) {
        result.push(block);
    }

    let newSurveyIndex = -1;

    for (const child of newChildren) {
        if (isSurveyBlock(child)) {
            newSurveyIndex++;
        }

        result.push(child);

        if (isSurveyBlock(child)) {
            pushInsertionsForSurveyIndex(
                insertions,
                newSurveyIndex,
                existingLastSurveyIndex,
                maxNewSurveyIndex,
                newHasTrailingContent,
                result,
            );
        }
    }

    newContainer.views = result;

    return newBuildingBlocks;
}

const PRESERVED_STEP_FIELDS = [
    'id',
    'guideId',
    'type',
    'pageId',
    'regexUrlRule',
    'elementPathRule',
    'triggerElementPathRule',
    'confirmationElementPathRule',
    'rank',
    'pollIds',
    'advanceMethod',
];

function serializeBlocks (blocks) {
    return {
        buildingBlocks: JSON.stringify(blocks),
        dom: JSON.stringify(BuildingBlock.buildingBlocksToDom(blocks)),
    };
}

function copyStylesFromSourceToTarget (sourceNode, targetNode) {
    if (Array.isArray(targetNode.properties)) {
        (sourceNode.properties || []).forEach((prop) => {
            if (!prop || !STYLE_PROPERTY_NAMES.has(prop.name)) return;

            const targetProp = targetNode.properties.find(({ name }) => name === prop.name);

            if (targetProp) {
                targetProp.type = prop.type;
                targetProp.value = prop.value;
            } else {
                targetNode.properties.push({ ...prop });
            }
        });
    }

    if (sourceNode.web && targetNode.web) {
        const targetDomId = targetNode.web.domId;

        WEB_STYLE_KEYS.forEach((key) => {
            if (sourceNode.web[key] === undefined) return;

            const value = key === 'pseudoStyles'
                ? mergePseudoStyles(sourceNode.web[key], targetNode.web[key])
                : cloneJson(sourceNode.web[key]);

            targetNode.web[key] = rescopeSurveyAnswerSelectors(value, targetDomId);
        });
        // web.description holds the (rating scale) endpoint label and its styling, so only carry it
        // when both sides are enabled endpoint labels. Otherwise a positional/domId match could push
        // a label's color/size onto an answer that isn't an endpoint label.
        if (isEnabledEndpointLabel(sourceNode) && isEnabledEndpointLabel(targetNode)) {
            copyDescriptionStyles(sourceNode.web.description, targetNode.web.description);
        }
    }
}

function copyDescriptionStyles (sourceDescription, targetDescription) {
    if (!Array.isArray(sourceDescription) || !Array.isArray(targetDescription)) return;

    sourceDescription.forEach((prop) => {
        if (!prop || !STYLE_PROPERTY_NAMES.has(prop.name)) return;

        const targetProp = targetDescription.find(({ name }) => name === prop.name);

        if (targetProp) {
            targetProp.type = prop.type;
            targetProp.value = prop.value;
        } else {
            targetDescription.push({ ...prop });
        }
    });
}

// A preserved answer block carries the previously-baked selectors, whose id can
// drift from the block's regenerated domId (APP-163793). Re-scope them to the
// block's own current domId so the :checked/:hover/:focus rules match again.
function rescopeSurveyAnswerSelectors (styles, domId) {
    if (!Array.isArray(styles) || typeof domId !== 'string' || !domId.startsWith('survey-answer__')) {
        return styles;
    }

    const replacement = `#${domId}`;

    return styles.map((style) => (
        style && typeof style.selector === 'string'
            ? { ...style, selector: style.selector.replace(/#survey-answer__[^\s,>+~]+/g, replacement) }
            : style
    ));
}

function cloneJson (value) {
    return JSON.parse(JSON.stringify(value));
}

function mergePseudoStyles (sourcePseudoStyles = [], targetPseudoStyles = []) {
    const sourceNonDarkStyles = cloneJson(sourcePseudoStyles).filter((style) => (
        !SurveyBlocksDarkMode.isDarkModeRadioLabelStyle(style)
    ));
    const targetNonDarkStyles = cloneJson(targetPseudoStyles).filter((style) => (
        !SurveyBlocksDarkMode.isDarkModeRadioLabelStyle(style)
    ));
    const generatedDarkModeStyles = targetPseudoStyles.filter((style) => (
        SurveyBlocksDarkMode.isDarkModeRadioLabelStyle(style)
    ));

    const preservedNonDarkStyles = sourceNonDarkStyles.length > 0
        ? sourceNonDarkStyles
        : targetNonDarkStyles;

    return [
        ...preservedNonDarkStyles,
        ...cloneJson(generatedDarkModeStyles),
    ];
}

function isCopyableSource (sourceChild, targetChild, matchedSources) {
    return Boolean(sourceChild) && !matchedSources.has(sourceChild) && sourceChild.widget === targetChild?.widget;
}

function findSourceByDomId (sourceViews, targetChild, matchedSources) {
    const domId = targetChild?.web?.domId;
    if (!domId) return null;

    const sourceChild = sourceViews.find((view) => view?.web?.domId === domId);

    return isCopyableSource(sourceChild, targetChild, matchedSources) ? sourceChild : null;
}

function findSourceByPosition (sourceViews, targetChild, index, matchedSources) {
    const sourceChild = sourceViews[index];

    return isCopyableSource(sourceChild, targetChild, matchedSources) ? sourceChild : null;
}

function isRatingAnswersContainer (node) {
    return (node.web?.domId || '').startsWith('survey-rating-scale-answers');
}

function ratingDisplayTypeChanged (sourceNode, targetNode) {
    if (!isRatingAnswersContainer(targetNode)) return false;

    return sourceNode.web?.ratingDisplayType !== targetNode.web?.ratingDisplayType;
}

function transferExistingStyles (sourceNode, targetNode) {
    if (!sourceNode || !targetNode || sourceNode.widget !== targetNode.widget) return;
    if (ratingDisplayTypeChanged(sourceNode, targetNode)) return;

    copyStylesFromSourceToTarget(sourceNode, targetNode);

    const sourceViews = sourceNode.views || [];
    const targetViews = targetNode.views || [];
    const matchedSources = new Set();
    const unmatchedTargets = [];

    targetViews.forEach((targetChild, index) => {
        const sourceChild = findSourceByDomId(sourceViews, targetChild, matchedSources);

        if (sourceChild) {
            matchedSources.add(sourceChild);
            transferExistingStyles(sourceChild, targetChild);
        } else {
            unmatchedTargets.push({ targetChild, index });
        }
    });

    // for blocks with unstable/regenerated domIds
    unmatchedTargets.forEach(({ targetChild, index }) => {
        const sourceChild = findSourceByPosition(sourceViews, targetChild, index, matchedSources);

        if (!sourceChild) return;

        matchedSources.add(sourceChild);
        transferExistingStyles(sourceChild, targetChild);
    });

    carryEndpointLabelStyles(sourceNode, targetNode);
}

function isEnabledEndpointLabel (view) {
    const description = view?.web?.description;

    if (!Array.isArray(description)) return false;

    return description.find((prop) => prop?.name === 'enabled')?.value === true;
}

function carryEndpointLabel (sourceView, targetView) {
    if (!isEnabledEndpointLabel(sourceView) || !isEnabledEndpointLabel(targetView)) return;

    copyDescriptionStyles(sourceView.web.description, targetView.web.description);
}

function carryEndpointLabelStyles (sourceNode, targetNode) {
    if (!isRatingAnswersContainer(targetNode)) return;

    const sourceViews = sourceNode.views || [];
    const targetViews = targetNode.views || [];

    if (!sourceViews.length || !targetViews.length) return;

    carryEndpointLabel(sourceViews[0], targetViews[0]);
    carryEndpointLabel(sourceViews[sourceViews.length - 1], targetViews[targetViews.length - 1]);
}

export function carryOverStylesOnBlocks (existingBuildingBlocks, newBuildingBlocks) {
    let existingBlocks;

    try {
        existingBlocks = typeof existingBuildingBlocks === 'string'
            ? JSON.parse(existingBuildingBlocks)
            : existingBuildingBlocks;
    } catch {
        return newBuildingBlocks;
    }

    if (!existingBlocks || !newBuildingBlocks) return newBuildingBlocks;

    transferExistingStyles(existingBlocks, newBuildingBlocks);

    return newBuildingBlocks;
}

export async function createGuideStepsFromLayout ({ guideId, layout, theme, guidePosition, snoozeEnabled = false, snoozeText, existingSteps = [] }) {
    const buildingBlocksForSteps = BuildingBlockLayouts.createSurveyBlocks({
        layout,
        guidePosition,
        themeStyles: theme.buildingBlocks,
        darkModeThemeStyles: theme.attributes?.darkMode?.buildingBlocks || {},
        platform: 'web',
        snoozeEnabled,
        snoozeText,
    });

    return Promise.all(
        buildingBlocksForSteps.map(async (stepBlocks, index) => {
            const existingStep = existingSteps[index];
            const autoAdvance = Boolean(layout[index]?.displayAttributes?.autoAdvance);

            if (existingStep) {
                const newBlocksClone = JSON.parse(JSON.stringify(stepBlocks));

                const mergedBlocks = mergeNonSurveyBlocks(
                    existingStep.buildingBlocks,
                    newBlocksClone,
                );

                const themeUnchanged = theme?.id != null && existingStep.attributes?.themeId === theme.id;
                const finalBlocks = themeUnchanged
                    ? carryOverStylesOnBlocks(existingStep.buildingBlocks, mergedBlocks)
                    : mergedBlocks;
                const preserved = Object.fromEntries(
                    PRESERVED_STEP_FIELDS.map((key) => [key, existingStep[key]]),
                );
                let serialized;

                try {
                    serialized = serializeBlocks(finalBlocks);
                } catch {
                    serialized = serializeBlocks(stepBlocks);
                }

                const attributes = {
                    ...existingStep.attributes,
                    themeId: theme.id,
                };
                if (autoAdvance) {
                    attributes.autoAdvance = true;
                } else {
                    delete attributes.autoAdvance;
                }

                return {
                    ...preserved,
                    ...serialized,
                    contentType: 'text/html',
                    attributes,
                };
            }

            const step = await createStep({ guide: guideId, type: 'building-block' });
            step.attributes.themeId = theme.id;
            if (autoAdvance) step.attributes.autoAdvance = true;
            step.content = null;
            step.pollIds = ['foobar'];
            step.contentType = 'text/html';
            Object.assign(step, serializeBlocks(stepBlocks));

            return step;
        }),
    );
}

export function canEditSurvey (survey, field, target) {
    return survey.appIds.reduce((acc, appId) => {
        return (
            acc &&
            $can(appId)
                .entity('guide', { entity: survey, field })
                .action('edit')
                .source(survey.status)
                .target(target || 'ANY')
        );
    }, true);
}

export function canPublishSurvey (survey) {
    return survey.appIds.reduce((acc, appId) => {
        return (
            acc &&
            $can(appId)
                .entity('guide', { entity: survey })
                .action('publish')
                .source('*')
                .target('*')
        );
    }, true);
}

export function canDeleteSurvey (survey) {
    const isAdmin = getActiveUser()?.isAdmin;
    const currentUserId = getActiveUser()?.id;
    const isOwner = survey.createdByUser.id === currentUserId;

    return isAdmin || isOwner;
}

export function canCreateSurveyInApp (appId) {
    return $can(appId)
        .entity('guide')
        .action('create')
        .source('ANY')
        .target('ANY');
}

export function getAppsWithSurveyCreatePermission (apps) {
    return apps.filter((app) => canCreateSurveyInApp(app.id));
}

export function getGuidePositionFromSteps (steps) {
    const isFooter = steps?.[0]?.buildingBlocks?.includes('"verticalAlignment":"Bottom Aligned"');

    return isFooter ? GUIDE_POSITION.FOOTER : GUIDE_POSITION.OVERLAY;
}

export function rebuildSurveyGuideSteps ({ guide, survey, items, snoozeEnabled, snoozeText, themes }) {
    const deliverySteps = survey.delivery?.[0]?.steps ?? [];
    const existingSteps = guide.steps ?? [];
    const themeId = existingSteps[0]?.attributes?.themeId;
    const theme = themes.find(({ id }) => id === themeId);
    const layout = buildSurveyLayout({ steps: deliverySteps, items: items ?? {} });

    return createGuideStepsFromLayout({
        guideId: guide.id,
        layout,
        theme,
        guidePosition: getGuidePositionFromSteps(existingSteps),
        snoozeEnabled,
        snoozeText,
        existingSteps,
    });
}
